// Admin bulk-registration review queue.
// Classification records a decision only — it never restricts or deletes an
// account. Restrictions live in the Account Integrity panel and carry their own
// reason + reviewer + timestamp.
import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Users, Search } from 'lucide-react';
import { reviewQueue, CLASSIFICATIONS, COHORTS, fmtDate, money, type ReviewRow, type Classification } from '@/lib/accountSecurity';

const tone = (c: string) => CLASSIFICATIONS.find((x) => x.value === c)?.tone || 'bg-slate-100 text-slate-700';

const AdminReviewQueue: React.FC = () => {
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [summary, setSummary] = useState<Record<string, any>>({});
  const [cohort, setCohort] = useState('');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [draft, setDraft] = useState<Record<string, { c: Classification; n: string }>>({});

  const load = useCallback(async () => {
    setBusy(true);
    const { data, error } = await reviewQueue.list(cohort);
    if (error) setMsg(error);
    else {
      setRows(data?.rows || []);
      setSummary({ total: data?.total, byClass: data?.by_classification, byCohort: data?.by_cohort, byRisk: data?.by_risk });
      setMsg('');
    }
    setBusy(false);
  }, [cohort]);

  useEffect(() => {
    void load();
  }, [load]);

  const rebuild = async () => {
    setBusy(true);
    const { error } = await reviewQueue.rebuild();
    setMsg(error || 'Queue rebuilt from live evidence.');
    await load();
  };

  const save = async (r: ReviewRow) => {
    const d = draft[r.user_id];
    if (!d) return;
    setBusy(true);
    const { error } = await reviewQueue.classify(r.user_id, d.c, d.n);
    setMsg(error ? `${r.user_id.slice(0, 8)}: ${error}` : `${r.user_id.slice(0, 8)} classified as ${d.c}.`);
    if (!error) setDraft((p) => ({ ...p, [r.user_id]: { c: d.c, n: '' } }));
    await load();
  };

  const filtered = rows.filter(
    (r) =>
      !q ||
      (r.email || '').toLowerCase().includes(q.toLowerCase()) ||
      r.user_id.toLowerCase().startsWith(q.toLowerCase())
  );

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
            <Users className="h-5 w-5 text-slate-700" /> Bulk-registration review queue
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            Shared-IP clusters, duplicate phone numbers, listing bursts and never-signed-in accounts. Identifiers are masked
            or hashed. Classifying an account records your decision and evidence — it does not suspend anyone.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={cohort}
            onChange={(e) => setCohort(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            {COHORTS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
                {summary.byCohort?.[c.value] ? ` (${summary.byCohort[c.value]})` : ''}
              </option>
            ))}
          </select>
          <button
            onClick={rebuild}
            disabled={busy}
            className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} /> Rebuild
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 text-xs">
        {Object.entries(summary.byClass || {}).map(([k, v]) => (
          <span key={k} className={`rounded-full px-2.5 py-1 font-semibold ${tone(k)}`}>
            {CLASSIFICATIONS.find((c) => c.value === k)?.label || k}: {String(v)}
          </span>
        ))}
        {Object.entries(summary.byRisk || {}).map(([k, v]) => (
          <span key={k} className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-700">
            risk {k}: {String(v)}
          </span>
        ))}
      </div>

      <div className="relative mt-4">
        <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by email or user id prefix"
          className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm"
        />
      </div>

      {msg && <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">{msg}</p>}

      <div className="mt-4 max-h-[36rem] space-y-3 overflow-y-auto pr-1">
        {filtered.map((r) => {
          const d = draft[r.user_id] || { c: r.classification, n: '' };
          return (
            <article key={r.user_id} className="rounded-xl border border-slate-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-mono text-sm font-semibold text-slate-900">{r.user_id}</p>
                  <p className="text-xs text-slate-500">{r.email}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className={`rounded-full px-2 py-0.5 font-semibold ${tone(r.classification)}`}>
                    {CLASSIFICATIONS.find((c) => c.value === r.classification)?.label}
                  </span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700">risk {r.risk_level}</span>
                  {(r.cohorts || []).map((c) => (
                    <span key={c} className="rounded-full bg-indigo-50 px-2 py-0.5 font-medium text-indigo-700">
                      {c}
                    </span>
                  ))}
                </div>
              </div>

              <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600 sm:grid-cols-4">
                <div><dt className="text-slate-400">Created</dt><dd>{fmtDate(r.account_created_at)}</dd></div>
                <div><dt className="text-slate-400">Type</dt><dd>{r.user_type}</dd></div>
                <div><dt className="text-slate-400">Email state</dt><dd>{r.email_verification_state}</dd></div>
                <div><dt className="text-slate-400">Phone</dt><dd>{r.phone_verified ? 'verified' : r.masked_phone || 'none'}</dd></div>
                <div><dt className="text-slate-400">Last sign-in</dt><dd>{fmtDate(r.last_sign_in_at)}</dd></div>
                <div><dt className="text-slate-400">Listings</dt><dd>{r.listing_count}</dd></div>
                <div><dt className="text-slate-400">Credits</dt><dd>{r.credits}</dd></div>
                <div><dt className="text-slate-400">Payments</dt><dd>{r.payment_count} / {money(r.payment_total)}</dd></div>
                <div className="col-span-2"><dt className="text-slate-400">Shared IP (hashed)</dt><dd className="font-mono">{r.shared_ip_hash || '—'} · {r.shared_ip_count} accts</dd></div>
                <div className="col-span-2"><dt className="text-slate-400">Phone hash</dt><dd className="font-mono">{r.phone_hash || '—'}</dd></div>
              </dl>

              {(r.indicators || []).length > 0 && (
                <ul className="mt-2 list-inside list-disc text-xs text-slate-600">
                  {r.indicators.map((i) => (
                    <li key={i}>{i}</li>
                  ))}
                </ul>
              )}

              {r.reviewer_notes && (
                <p className="mt-2 rounded bg-slate-50 px-2 py-1 text-xs text-slate-600">
                  Note: {r.reviewer_notes} — {r.reviewer_email} · {fmtDate(r.classified_at)}
                </p>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <select
                  value={d.c}
                  onChange={(e) => setDraft((p) => ({ ...p, [r.user_id]: { ...d, c: e.target.value as Classification } }))}
                  className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
                >
                  {CLASSIFICATIONS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <input
                  value={d.n}
                  onChange={(e) => setDraft((p) => ({ ...p, [r.user_id]: { ...d, n: e.target.value } }))}
                  placeholder="Reviewer note (required, min 8 chars)"
                  className="min-w-[16rem] flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs"
                />
                <button
                  onClick={() => save(r)}
                  disabled={busy || (d.c !== 'unreviewed' && d.n.trim().length < 8)}
                  className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
                >
                  Save classification
                </button>
              </div>
            </article>
          );
        })}
        {filtered.length === 0 && <p className="py-8 text-center text-sm text-slate-500">No accounts in this cohort.</p>}
      </div>
    </section>
  );
};

export default AdminReviewQueue;
